// Several received files → one .zip, streamed from disk to disk (fflate, stored entries: the
// typical payload — photos, videos — is already compressed, and storing keeps it fast on phones).

import { Zip, ZipPassThrough } from 'fflate';
import { openSink } from './sinks';

/** "photo.jpg", "photo.jpg" → "photo.jpg", "photo (2).jpg" */
export function uniqueNames(names: string[]): string[] {
  const seen = new Map<string, number>();
  return names.map((name) => {
    const n = (seen.get(name.toLowerCase()) ?? 0) + 1;
    seen.set(name.toLowerCase(), n);
    if (n === 1) return name;
    const dot = name.lastIndexOf('.');
    return dot > 0 ? `${name.slice(0, dot)} (${n})${name.slice(dot)}` : `${name} (${n})`;
  });
}

export async function zipFiles(files: File[], zipName: string, key: string): Promise<File> {
  const sink = await openSink(`${key}-zip`);
  let offset = 0;
  let failure: Error | null = null;
  const finished = new Promise<void>((resolve) => {
    const zip = new Zip((err, data, final) => {
      if (err) failure = err;
      else {
        void sink.write(offset, data);
        offset += data.byteLength;
      }
      if (final || err) resolve();
    });
    void (async () => {
      const names = uniqueNames(files.map((f) => f.name));
      for (const [i, file] of files.entries()) {
        const entry = new ZipPassThrough(names[i]);
        entry.mtime = file.lastModified || Date.now();
        zip.add(entry);
        const reader = file.stream().getReader();
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          entry.push(value, false);
          await sink.drained();
        }
        entry.push(new Uint8Array(0), true);
      }
      zip.end();
    })().catch((e: Error) => {
      failure = e;
      resolve();
    });
  });
  await finished;
  if (failure) throw failure;
  return sink.close(zipName, 'application/zip');
}
